// Chassis Locations — the front end for the Anytrek snapshot callable.
//
// The backend for this page has existed since 2026-07-25 (anytrek-contract /
// normalizer / client / snapshot-service / firestore-store / handler /
// function); the plan it came from was scoped to the backend only, so no page
// was ever built. This module is that page.
//
// Two rules shaped it:
//   1. A chassis the tracker cannot place is still a chassis. Assets with no
//      fix are counted, listed and selectable — dropping them from the list
//      because they cannot be drawn is how a missing trailer stays missing.
//   2. The map is an enhancement, not the page. No Maps key, a blocked script
//      or a dead network degrades to the list and the detail panel, both of
//      which carry every field the contract returns.

import { formatLocalizedDate } from './i18n.mjs?v=20260806.100';
import { loadGoogleMaps } from './google-maps-loader.mjs?v=20260806.100';
import { mapDisplayOptions, mapLoadMessage } from './dashboard-map-tab.mjs?v=20260806.100';

const EMPTY = '—';

// Long Beach, the same fallback the dashboard map tab uses.
const FALLBACK_CENTER = Object.freeze({ lat: 33.7701, lng: -118.1937 });

// Above this the chassis is reported as moving rather than parked. Trackers
// report small non-zero speeds while stationary, so zero is the wrong cut.
const MOVING_SPEED_KPH = 1;

// Silence longer than this reads as offline. The vendor's own `online` flag came
// back null for every device in the live account, so a page that trusts only
// that flag calls a tracker last heard from in 2025 "parked" — the one reading
// that hides a missing chassis. Measured against the snapshot's own fetch time,
// not the clock, so a saved snapshot is judged as of when it was taken.
const OFFLINE_AFTER_MS = 24 * 60 * 60 * 1000;

const COMPASS = Object.freeze([
  'N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE',
  'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW',
]);

const COPY = Object.freeze({
  en: Object.freeze({
    title: 'Chassis Locations',
    refresh: 'Refresh',
    refreshing: 'Refreshing…',
    updated: 'Updated {{time}}',
    neverUpdated: 'Never updated',
    stale: 'Saved snapshot',
    staleDetail: 'Anytrek could not be reached. Showing the last saved snapshot.',
    total: 'Chassis',
    moving: 'Moving',
    idle: 'Parked',
    offline: 'Offline',
    unmapped: 'No fix',
    searchLabel: 'Search chassis',
    searchPlaceholder: 'Name, plate, VIN or address',
    filter: 'Filter',
    all: 'All',
    listLabel: 'Chassis list',
    noMatches: 'No chassis matches this search.',
    empty: 'Anytrek returned no chassis for this account.',
    emptyHint: 'Once trackers report in, they appear here automatically.',
    error: 'Chassis locations could not be loaded.',
    errorHint: 'Choose Try again once the connection is back.',
    retry: 'Try again',
    mapLabel: 'Chassis map',
    mapLoading: 'Loading map…',
    mapListOnly: 'Map unavailable — the list and details below still work.',
    selectPrompt: 'Choose a chassis to see its full record.',
    noFix: 'This chassis has not reported a position.',
    moreDetails: 'More details',
    identity: 'Identity',
    position: 'Position',
    telemetry: 'Telemetry',
    displayName: 'Name',
    licensePlate: 'License Plate',
    vin: 'VIN',
    make: 'Make',
    model: 'Model',
    year: 'Year',
    vehicleId: 'Vehicle ID',
    deviceId: 'Device ID',
    trackerModel: 'Tracker',
    address: 'Address',
    state: 'State',
    landmark: 'Landmark',
    landmarkEntered: 'Entered Landmark',
    latitude: 'Latitude',
    longitude: 'Longitude',
    heading: 'Heading',
    altitude: 'Altitude',
    locationAt: 'Position Time',
    online: 'Online',
    speed: 'Speed',
    mileage: 'Odometer',
    ignition: 'Ignition',
    battery: 'Battery',
    voltage: 'Voltage',
    charging: 'Charging',
    signal: 'Signal',
    eventCode: 'Event Code',
    reportAt: 'Last Report',
    yes: 'Yes',
    no: 'No',
    on: 'On',
    off: 'Off',
  }),
  'zh-CN': Object.freeze({
    title: '车架定位',
    refresh: '刷新',
    refreshing: '刷新中…',
    updated: '更新于 {{time}}',
    neverUpdated: '尚未更新',
    stale: '离线快照',
    staleDetail: '无法连接 Anytrek，显示最近一次保存的快照。',
    total: '车架',
    moving: '行驶中',
    idle: '停放',
    offline: '离线',
    unmapped: '无定位',
    searchLabel: '搜索车架',
    searchPlaceholder: '名称、车牌、VIN 或地址',
    filter: '筛选',
    all: '全部',
    listLabel: '车架列表',
    noMatches: '没有符合搜索条件的车架。',
    empty: 'Anytrek 未返回任何车架。',
    emptyHint: '追踪器上报后会自动出现在这里。',
    error: '无法加载车架定位。',
    errorHint: '连接恢复后点击「重试」。',
    retry: '重试',
    mapLabel: '车架地图',
    mapLoading: '正在加载地图…',
    mapListOnly: '地图不可用 —— 下方列表和详情仍可使用。',
    selectPrompt: '选择一台车架查看完整记录。',
    noFix: '该车架尚未上报位置。',
    moreDetails: '详细信息',
    identity: '身份',
    position: '位置',
    telemetry: '遥测',
    displayName: '名称',
    licensePlate: '车牌',
    vin: 'VIN',
    make: '厂牌',
    model: '型号',
    year: '年份',
    vehicleId: '车辆编号',
    deviceId: '设备编号',
    trackerModel: '追踪器',
    address: '地址',
    state: '州',
    landmark: '地标',
    landmarkEntered: '进入地标',
    latitude: '纬度',
    longitude: '经度',
    heading: '朝向',
    altitude: '海拔',
    locationAt: '定位时间',
    online: '在线',
    speed: '速度',
    mileage: '里程',
    ignition: '点火',
    battery: '电量',
    voltage: '电压',
    charging: '充电中',
    signal: '信号',
    eventCode: '事件代码',
    reportAt: '最后上报',
    yes: '是',
    no: '否',
    on: '开',
    off: '关',
  }),
});

function escapeHtml(value) {
  return String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');
}

export function chassisCopy(locale = 'en') {
  return COPY[locale] || COPY.en;
}

function numeric(value) {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function trimmed(value) {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

export function chassisHasFix(asset) {
  return numeric(asset?.latitude) !== null && numeric(asset?.longitude) !== null;
}

// Age of the last report as of `referenceIso`, or null when either end is
// unreadable — an unknown age must not be mistaken for a fresh one.
export function chassisReportAgeMs(asset, referenceIso) {
  const reference = Date.parse(referenceIso ?? '');
  const reported = Date.parse(asset?.report_at ?? '');
  if (!Number.isFinite(reference) || !Number.isFinite(reported)) return null;
  return reference - reported;
}

// Offline wins over speed: a tracker that stopped reporting at 60 km/h is not
// "moving", it is missing, and calling it moving hides exactly that. The same
// applies to a tracker that stopped reporting at rest — it is not parked.
export function chassisMovementState(asset, { referenceIso = null } = {}) {
  if (asset?.online === false) return 'offline';
  const age = chassisReportAgeMs(asset, referenceIso);
  if (age !== null && age > OFFLINE_AFTER_MS) return 'offline';
  const speed = numeric(asset?.speed_kph);
  return speed !== null && speed > MOVING_SPEED_KPH ? 'moving' : 'idle';
}

// A tracker reporting 3 km/h whose last word arrived two and a half hours ago is
// shown as moving — that is genuinely the last thing it said — but it is not a
// reason to keep re-reading the vendor. Live polling asks the narrower question:
// is anything moving *and* still talking?
const LIVE_MOVEMENT_WINDOW_MS = 15 * 60 * 1000;

export function chassisHasLiveMovement(model) {
  const referenceIso = model?.fetchedAt ?? null;
  return (model?.assets ?? []).some((asset) => {
    if (asset.movement !== 'moving') return false;
    const age = chassisReportAgeMs(asset, referenceIso);
    return age !== null && age <= LIVE_MOVEMENT_WINDOW_MS;
  });
}

export function chassisSearchHaystack(asset) {
  return [
    asset?.display_name,
    asset?.vehicle_name,
    asset?.license_plate,
    asset?.vin,
    asset?.device_id,
    asset?.vehicle_id,
    asset?.address,
    asset?.state,
    asset?.landmark_name,
  ].map((value) => String(value ?? '').toLowerCase()).join(' ');
}

// The five buckets the KPI row counts, so filtering and counting can never
// disagree about what "Offline" means.
export const CHASSIS_FILTERS = Object.freeze(['all', 'moving', 'idle', 'offline', 'unmapped']);

function matchesChassisFilter(asset, statusFilter) {
  if (statusFilter === 'all') return true;
  if (statusFilter === 'unmapped') return !asset.mapped;
  return asset.movement === statusFilter;
}

export function chassisPageModel({
  snapshot = null,
  query = '',
  selectedDeviceId = '',
  error = null,
  statusFilter = 'all',
} = {}) {
  const assets = Array.isArray(snapshot?.assets) ? snapshot.assets : [];
  const normalizedQuery = String(query || '').trim().toLowerCase();
  const referenceIso = snapshot?.fetched_at ?? null;
  const activeFilter = CHASSIS_FILTERS.includes(statusFilter) ? statusFilter : 'all';
  // Freshest first. Anytrek hands the list back in name order, which buries the
  // trackers that just reported under two years of silent ones — the list is
  // read top-down, so the top has to be where something is happening. Trackers
  // that have never reported sort last rather than first, and ties fall back to
  // name so the order is stable between refreshes.
  const reportedAt = (asset) => {
    const parsed = Date.parse(asset?.report_at ?? asset?.location_at ?? '');
    return Number.isFinite(parsed) ? parsed : Number.NEGATIVE_INFINITY;
  };
  const rows = assets.map((asset) => ({
    ...asset,
    movement: chassisMovementState(asset, { referenceIso }),
    mapped: chassisHasFix(asset),
  })).sort((left, right) => reportedAt(right) - reportedAt(left)
    || left.display_name.localeCompare(right.display_name, 'en', { sensitivity: 'base' }));
  const visible = rows.filter((asset) => matchesChassisFilter(asset, activeFilter)
    && (!normalizedQuery || chassisSearchHaystack(asset).includes(normalizedQuery)));
  const counts = {
    total: rows.length,
    moving: rows.filter((asset) => asset.movement === 'moving').length,
    idle: rows.filter((asset) => asset.movement === 'idle').length,
    offline: rows.filter((asset) => asset.movement === 'offline').length,
    unmapped: rows.filter((asset) => !asset.mapped).length,
  };
  const selected = visible.find((asset) => asset.device_id === selectedDeviceId)
    ?? visible.find((asset) => asset.mapped)
    ?? visible[0]
    ?? null;
  return Object.freeze({
    error: error ? String(error) : null,
    stale: snapshot?.stale === true,
    warning: snapshot?.warning ?? null,
    fetchedAt: snapshot?.fetched_at ?? null,
    query: String(query || ''),
    statusFilter: activeFilter,
    counts: Object.freeze(counts),
    assets: Object.freeze(rows),
    visible: Object.freeze(visible),
    selectedDeviceId: selected?.device_id ?? '',
    selected,
  });
}

function formatBoolean(value, copy, { trueLabel, falseLabel } = {}) {
  if (value === true) return trueLabel ?? copy.yes;
  if (value === false) return falseLabel ?? copy.no;
  return EMPTY;
}

function formatNumber(value, { digits = 0, suffix = '' } = {}) {
  const normalized = numeric(value);
  if (normalized === null) return EMPTY;
  return `${normalized.toFixed(digits)}${suffix}`;
}

export function formatHeading(value) {
  const normalized = numeric(value);
  if (normalized === null) return EMPTY;
  const point = COMPASS[Math.round((normalized % 360) / 22.5) % 16];
  return `${normalized.toFixed(0)}° ${point}`;
}

// The contract declares kilometres per hour and metres. Both are rendered with
// their unit visible rather than converted, because the declared units have not
// yet been checked against a live Anytrek response.
export function formatSpeed(value) {
  return formatNumber(value, { digits: 0, suffix: ' km/h' });
}

export function formatMileage(value) {
  const metres = numeric(value);
  if (metres === null) return EMPTY;
  return `${(metres / 1000).toLocaleString('en-US', { maximumFractionDigits: 1 })} km`;
}

function formatTime(value, locale) {
  const formatted = value ? formatLocalizedDate(value, locale) : '';
  return formatted || EMPTY;
}

function statusChip(asset, copy) {
  const label = copy[asset.movement] ?? asset.movement;
  return `<span class="chassis-chip chassis-chip--${escapeHtml(asset.movement)}">${escapeHtml(label)}</span>`;
}

function assetListItemMarkup(asset, { copy, selectedDeviceId, locale = 'en' }) {
  const selected = asset.device_id === selectedDeviceId;
  const secondary = trimmed(asset.license_plate) || trimmed(asset.vin) || asset.device_id;
  const location = trimmed(asset.address) || trimmed(asset.landmark_name)
    || (asset.mapped ? `${asset.latitude.toFixed(4)}, ${asset.longitude.toFixed(4)}` : copy.noFix);
  const battery = numeric(asset.battery_percent);
  return `<li>
    <button type="button" class="chassis-row${selected ? ' is-selected' : ''}" data-chassis-row="${escapeHtml(asset.device_id)}" aria-pressed="${selected ? 'true' : 'false'}">
      <span class="chassis-row__head">
        <span class="chassis-row__name">${escapeHtml(asset.display_name)}</span>
        ${statusChip(asset, copy)}
      </span>
      <span class="chassis-row__meta">${escapeHtml(secondary)}</span>
      <span class="chassis-row__location">${escapeHtml(location)}</span>
      <span class="chassis-row__foot">
        ${asset.mapped ? '' : `<span class="chassis-row__flag">${escapeHtml(copy.unmapped)}</span>`}
        ${battery === null ? '' : `<span class="chassis-row__battery" data-low="${battery <= 20 ? 'true' : 'false'}">${escapeHtml(formatNumber(battery, { suffix: '%' }))}</span>`}
        <span class="chassis-row__time">${escapeHtml(formatTime(asset.location_at, locale))}</span>
      </span>
    </button>
  </li>`;
}

function fieldMarkup(label, value) {
  return `<div class="chassis-field">
    <dt>${escapeHtml(label)}</dt>
    <dd>${escapeHtml(value)}</dd>
  </div>`;
}

export function chassisAssetDetailMarkup({ asset = null, locale = 'en' } = {}) {
  const copy = chassisCopy(locale);
  if (!asset) {
    return `<p class="chassis-detail__prompt">${escapeHtml(copy.selectPrompt)}</p>`;
  }
  const movement = asset.movement || chassisMovementState(asset);
  const mapped = asset.mapped ?? chassisHasFix(asset);
  // What a dispatcher needs at a glance: where it is, whether it is still
  // talking, and when it last did. Everything else is real but rarely the
  // question, so it goes behind the disclosure rather than off the page.
  const headline = [
    [copy.address, trimmed(asset.address)
      ?? trimmed(asset.landmark_name)
      ?? (mapped ? `${asset.latitude.toFixed(5)}, ${asset.longitude.toFixed(5)}` : EMPTY)],
    [copy.reportAt, formatTime(asset.report_at, locale)],
    [copy.battery, formatNumber(asset.battery_percent, { suffix: '%' })],
    [copy.speed, formatSpeed(asset.speed_kph)],
  ];
  const groups = [
    [copy.identity, [
      [copy.displayName, trimmed(asset.display_name) ?? EMPTY],
      [copy.licensePlate, trimmed(asset.license_plate) ?? EMPTY],
      [copy.vin, trimmed(asset.vin) ?? EMPTY],
      [copy.make, trimmed(asset.make) ?? EMPTY],
      [copy.model, trimmed(asset.model) ?? EMPTY],
      [copy.year, trimmed(asset.year) ?? EMPTY],
      [copy.vehicleId, trimmed(asset.vehicle_id) ?? EMPTY],
      [copy.deviceId, trimmed(asset.device_id) ?? EMPTY],
      [copy.trackerModel, trimmed(asset.tracker_model) ?? EMPTY],
    ]],
    [copy.position, [
      [copy.address, trimmed(asset.address) ?? EMPTY],
      [copy.state, trimmed(asset.state) ?? EMPTY],
      [copy.landmark, trimmed(asset.landmark_name) ?? EMPTY],
      [copy.landmarkEntered, formatTime(asset.landmark_entered_at, locale)],
      [copy.latitude, mapped ? asset.latitude.toFixed(6) : EMPTY],
      [copy.longitude, mapped ? asset.longitude.toFixed(6) : EMPTY],
      [copy.heading, formatHeading(asset.heading_degrees)],
      [copy.altitude, formatNumber(asset.altitude_meters, { digits: 0, suffix: ' m' })],
      [copy.locationAt, formatTime(asset.location_at, locale)],
    ]],
    [copy.telemetry, [
      [copy.online, formatBoolean(asset.online, copy)],
      [copy.speed, formatSpeed(asset.speed_kph)],
      [copy.mileage, formatMileage(asset.mileage_meters)],
      [copy.ignition, formatBoolean(asset.ignition_on, copy, { trueLabel: copy.on, falseLabel: copy.off })],
      [copy.battery, formatNumber(asset.battery_percent, { suffix: '%' })],
      [copy.voltage, formatNumber(asset.voltage, { digits: 1, suffix: ' V' })],
      [copy.charging, formatBoolean(asset.charging, copy)],
      [copy.signal, formatNumber(asset.signal_percent, { suffix: '%' })],
      [copy.eventCode, asset.event_code === null || asset.event_code === undefined ? EMPTY : String(asset.event_code)],
      [copy.reportAt, formatTime(asset.report_at, locale)],
    ]],
  ];
  return `<header class="chassis-detail__head">
      <div class="chassis-detail__heading">
        <h2 class="chassis-detail__title">${escapeHtml(asset.display_name)}</h2>
        <span class="chassis-chip chassis-chip--${escapeHtml(movement)}">${escapeHtml(copy[movement] ?? movement)}</span>
      </div>
    </header>
    ${mapped ? '' : `<p class="chassis-detail__flag" role="status">${escapeHtml(copy.noFix)}</p>`}
    <dl class="chassis-detail__facts">${headline.map(([label, value]) => fieldMarkup(label, value)).join('')}</dl>
    <details class="chassis-detail__more">
      <summary>${escapeHtml(copy.moreDetails)}</summary>
      ${groups.map(([heading, fields]) => `<section class="chassis-detail__group">
        <h3>${escapeHtml(heading)}</h3>
        <dl class="chassis-detail__grid">${fields.map(([label, value]) => fieldMarkup(label, value)).join('')}</dl>
      </section>`).join('')}
    </details>`;
}

// The rail body is repainted on its own whenever the query changes, so the
// search input beside it keeps focus and the map instance is never rebuilt.
export function chassisRailBodyMarkup({ model, locale = 'en' } = {}) {
  const copy = chassisCopy(locale);
  const counts = model?.counts ?? { total: 0 };
  const list = model?.visible ?? [];
  // A failed load is not an empty fleet. Saying "no chassis for this account"
  // when the call never returned would report chassis as gone.
  if (model?.error) {
    return `<div class="chassis-empty"><p>${escapeHtml(copy.error)}</p><p class="chassis-empty__hint">${escapeHtml(copy.errorHint)}</p></div>`;
  }
  if (counts.total === 0) {
    return `<div class="chassis-empty"><p>${escapeHtml(copy.empty)}</p><p class="chassis-empty__hint">${escapeHtml(copy.emptyHint)}</p></div>`;
  }
  if (list.length === 0) {
    return `<div class="chassis-empty"><p>${escapeHtml(copy.noMatches)}</p></div>`;
  }
  return `<ul class="chassis-list" data-chassis-list>${list.map((asset) => assetListItemMarkup(asset, {
    copy,
    locale,
    selectedDeviceId: model?.selectedDeviceId ?? '',
  })).join('')}</ul>`;
}

// A disclosure rather than a bespoke popup: open, close, Escape and click-away
// all come from the browser, and the counts show what each choice would leave.
export function chassisFilterMarkup({ model, locale = 'en' } = {}) {
  const copy = chassisCopy(locale);
  const counts = model?.counts ?? { total: 0, moving: 0, idle: 0, offline: 0, unmapped: 0 };
  const active = model?.statusFilter ?? 'all';
  const labelFor = (key) => (key === 'all' ? copy.all : copy[key] ?? key);
  const countFor = (key) => (key === 'all' ? counts.total : counts[key] ?? 0);
  // Each choice carries the colour its KPI carries at the top of the page, so
  // "Offline" means the same grey in both places and the eye can move between
  // the counter and the filter without re-learning the palette.
  return `<details class="chassis-filter" data-chassis-filter data-tone="${escapeHtml(active)}"${active === 'all' ? '' : ' data-active="true"'}>
    <summary class="chassis-filter__toggle">
      <span class="chassis-filter__label">${escapeHtml(copy.filter)}</span>
      <span class="chassis-filter__value"><i class="chassis-filter__dot" aria-hidden="true"></i>${escapeHtml(labelFor(active))}</span>
    </summary>
    <div class="chassis-filter__menu" role="group" aria-label="${escapeHtml(copy.filter)}">
      ${CHASSIS_FILTERS.map((key) => `<button type="button" class="chassis-filter__option${key === active ? ' is-active' : ''}" data-tone="${escapeHtml(key)}" data-chassis-filter-value="${escapeHtml(key)}" aria-pressed="${key === active ? 'true' : 'false'}">
        <span class="chassis-filter__name"><i class="chassis-filter__dot" aria-hidden="true"></i>${escapeHtml(labelFor(key))}</span>
        <span class="chassis-filter__count">${escapeHtml(String(countFor(key)))}</span>
      </button>`).join('')}
    </div>
  </details>`;
}

function kpiMarkup(label, value, tone) {
  return `<div class="chassis-kpi chassis-kpi--${escapeHtml(tone)}">
    <span class="chassis-kpi__value">${escapeHtml(String(value))}</span>
    <span class="chassis-kpi__label">${escapeHtml(label)}</span>
  </div>`;
}

/**
 * What the chassis page puts on the chrome bar (Alan, 2026-08-09: "把这个车架定位的
 * status 和刷新按钮和时间放到 boarder 内部。删除车架定位这四个字").
 *
 * The page had a hero of its own - a title, a row of counts and the refresh
 * control - stacked above the map, which is most of a screen of chrome before the
 * first pin. The title said what the menu icon already said. The rest belongs on
 * the bar the whole product now shares.
 */
export function chassisMapBarMarkup({ model = null, locale = 'zh' } = {}) {
  const copy = chassisCopy(locale);
  const counts = model?.counts ?? { total: 0, moving: 0, idle: 0, offline: 0, unmapped: 0 };
  const updated = model?.fetchedAt
    ? copy.updated.replace('{{time}}', formatLocalizedDate(model.fetchedAt, locale))
    : copy.neverUpdated;
  return `<div class="chassis-map-bar">
    <div class="chassis-map-kpis">
      ${kpiMarkup(copy.total, counts.total, 'total')}
      ${kpiMarkup(copy.moving, counts.moving, 'moving')}
      ${kpiMarkup(copy.idle, counts.idle, 'idle')}
      ${kpiMarkup(copy.offline, counts.offline, 'offline')}
      ${kpiMarkup(copy.unmapped, counts.unmapped, 'unmapped')}
    </div>
    <span class="chassis-map-hero__updated" data-chassis-updated>${escapeHtml(updated)}</span>
    ${model?.stale ? `<span class="chassis-map-hero__stale" title="${escapeHtml(copy.staleDetail)}">${escapeHtml(copy.stale)}</span>` : ''}
    <button type="button" class="chassis-refresh" data-chassis-refresh>${escapeHtml(copy.refresh)}</button>
  </div>`;
}

export function chassisMapPageMarkup({ model, locale = 'en' } = {}) {
  const copy = chassisCopy(locale);
  const updated = model?.fetchedAt
    ? copy.updated.replace('{{time}}', formatLocalizedDate(model.fetchedAt, locale))
    : copy.neverUpdated;
  const counts = model?.counts ?? { total: 0, moving: 0, idle: 0, offline: 0, unmapped: 0 };
  // One compact bar — title, counts and controls on a single line — so the
  // remaining height belongs to the map. The map is the page.
  return `<section class="chassis-map-page" data-chassis-map-page aria-label="${escapeHtml(copy.title)}">
    ${model?.stale ? `<p class="chassis-map-banner" role="status">${escapeHtml(copy.staleDetail)}</p>` : ''}
    ${model?.error ? `<p class="chassis-map-banner chassis-map-banner--error" role="alert">${escapeHtml(copy.error)} <button type="button" class="chassis-retry" data-chassis-refresh>${escapeHtml(copy.retry)}</button></p>` : ''}
    <div class="chassis-map-body">
      <aside class="chassis-map-rail" aria-label="${escapeHtml(copy.listLabel)}">
        <div class="chassis-rail-head" data-chassis-filter-host>${chassisFilterMarkup({ model, locale })}</div>
        <label class="chassis-search">
          <input type="search" data-chassis-search placeholder="${escapeHtml(copy.searchPlaceholder)}" value="${escapeHtml(model?.query ?? '')}" aria-label="${escapeHtml(copy.searchLabel)}">
        </label>
        <div class="chassis-rail-body" data-chassis-rail-body>${chassisRailBodyMarkup({ model, locale })}</div>
      </aside>
      <div class="chassis-map-stage">
        <div class="chassis-map-canvas" data-chassis-canvas aria-label="${escapeHtml(copy.mapLabel)}"></div>
        <p class="chassis-map-status" data-chassis-map-status role="status">${escapeHtml(copy.mapLoading)}</p>
        <article class="chassis-detail" data-chassis-detail aria-live="polite">${chassisAssetDetailMarkup({ asset: model?.selected ?? null, locale })}</article>
      </div>
    </div>
  </section>`;
}

const MARKER_COLORS = Object.freeze({
  moving: '#0b6bff',
  idle: '#00b96b',
  offline: '#98a2b3',
});

function markerIcon(maps, asset, selected) {
  const SizeClass = maps?.Size;
  const PointClass = maps?.Point;
  const fill = MARKER_COLORS[asset.movement] || MARKER_COLORS.idle;
  const size = selected ? 34 : 26;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" width="${size}" height="${size}">
    <circle cx="16" cy="16" r="11" fill="${fill}" stroke="#fff" stroke-width="3"/>
    ${selected ? '<circle cx="16" cy="16" r="14.5" fill="none" stroke="#0b1538" stroke-width="1.5" opacity=".55"/>' : ''}
  </svg>`;
  const icon = { url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}` };
  if (typeof SizeClass === 'function') icon.scaledSize = new SizeClass(size, size);
  if (typeof PointClass === 'function') {
    icon.anchor = new PointClass(size / 2, size / 2);
    // Name under the pin. Over satellite imagery a bare dot says a chassis is
    // somewhere here; the name says which one, without a click.
    icon.labelOrigin = new PointClass(size / 2, size + 9);
  }
  return icon;
}

function markerLabel(asset, selected) {
  return {
    text: asset.display_name,
    className: `chassis-marker-label${selected ? ' is-selected' : ''}`,
    color: '#0b1538',
    fontSize: '11px',
    fontWeight: '600',
  };
}

export function mountChassisMapPage(root, {
  model,
  locale = 'en',
  apiKey = globalThis.KM_TMS_GOOGLE_MAPS_API_KEY || '',
  loader = loadGoogleMaps,
  onSelect = () => {},
  onSearch = () => {},
  onFilter = () => {},
  onRefresh = async () => {},
} = {}) {
  const host = root?.querySelector?.('[data-chassis-map-page]');
  const canvas = host?.querySelector?.('[data-chassis-canvas]');
  const status = host?.querySelector?.('[data-chassis-map-status]');
  const detail = host?.querySelector?.('[data-chassis-detail]');
  if (!host || !canvas || !status || !detail || typeof loader !== 'function') {
    throw new TypeError('Chassis map page options are invalid.');
  }
  const copy = chassisCopy(locale);
  const controller = new AbortController();
  const { signal } = controller;
  let destroyed = false;
  let currentModel = model ?? chassisPageModel({});
  let map = null;
  let maps = null;
  let infoWindow = null;
  // The page opens on the whole fleet, not on one chassis. A card that appears
  // by itself over an alphabetically-first tracker nobody asked about is noise,
  // and opening it drags the map off the view that was just fitted to show
  // everything. The card is a consequence of picking something.
  let detailOpen = false;
  const markers = new Map();

  const setStatus = (message, state = '') => {
    if (destroyed) return;
    status.textContent = message;
    status.dataset.state = state;
    status.hidden = !message;
  };

  // The record belongs beside the chassis it describes, with a tail pointing at
  // the pin — so it is read as "this one" rather than "some one". An InfoWindow
  // gets that anchoring, the tail, and the follow-on-pan for free. The static
  // panel underneath is the fallback for when there is no map to anchor to: no
  // map, no marker, and the record still has to be readable.
  const paintDetail = () => {
    const markup = chassisAssetDetailMarkup({ asset: currentModel.selected, locale });
    const anchor = markers.get(currentModel.selectedDeviceId);
    if (infoWindow && anchor && map) {
      detail.hidden = true;
      if (!detailOpen) {
        infoWindow.close();
        return;
      }
      infoWindow.setContent(`<div class="chassis-detail chassis-detail--anchored">${markup}</div>`);
      infoWindow.open({ anchor, map });
      return;
    }
    detail.innerHTML = markup;
    detail.hidden = false;
  };

  const paintSelection = () => {
    for (const row of host.querySelectorAll('[data-chassis-row]')) {
      const selected = row.dataset.chassisRow === currentModel.selectedDeviceId;
      row.classList.toggle('is-selected', selected);
      row.setAttribute('aria-pressed', selected ? 'true' : 'false');
    }
    for (const [deviceId, marker] of markers) {
      const asset = currentModel.assets.find((item) => item.device_id === deviceId);
      if (!asset || !maps) continue;
      const selected = deviceId === currentModel.selectedDeviceId;
      marker.setIcon?.(markerIcon(maps, asset, selected));
      marker.setLabel?.(markerLabel(asset, selected));
      marker.setZIndex?.(selected ? 999 : 1);
    }
    const selected = currentModel.selected;
    if (map && selected?.mapped) {
      map.panTo?.({ lat: selected.latitude, lng: selected.longitude });
      if ((map.getZoom?.() ?? 0) < 12) map.setZoom?.(13);
    }
    paintDetail();
  };

  const drawMarkers = () => {
    if (!map || !maps) return;
    const MarkerClass = maps.Marker;
    if (typeof MarkerClass !== 'function') {
      setStatus(copy.mapListOnly, 'error');
      return;
    }
    for (const marker of markers.values()) marker.setMap?.(null);
    markers.clear();
    const bounds = typeof maps.LatLngBounds === 'function' ? new maps.LatLngBounds() : null;
    let plotted = 0;
    for (const asset of currentModel.assets) {
      if (!asset.mapped) continue;
      const position = { lat: asset.latitude, lng: asset.longitude };
      const selected = asset.device_id === currentModel.selectedDeviceId;
      const marker = new MarkerClass({
        map,
        position,
        title: asset.display_name,
        icon: markerIcon(maps, asset, selected),
        label: markerLabel(asset, selected),
      });
      marker.addListener?.('click', () => select(asset.device_id));
      markers.set(asset.device_id, marker);
      bounds?.extend?.(position);
      plotted += 1;
    }
    if (plotted === 0) {
      map.setCenter?.(FALLBACK_CENTER);
      map.setZoom?.(10);
      setStatus(currentModel.counts.total === 0 ? copy.empty : copy.noFix, 'empty');
      return;
    }
    if (bounds && plotted > 1) map.fitBounds?.(bounds, 48);
    else if (plotted === 1) {
      const only = currentModel.assets.find((asset) => asset.mapped);
      map.setCenter?.({ lat: only.latitude, lng: only.longitude });
      map.setZoom?.(13);
    }
    setStatus('');
    // The markers now exist, so the record can move off the fallback panel and
    // onto the pin it belongs to.
    paintDetail();
  };

  function select(deviceId) {
    if (destroyed || !deviceId) return;
    detailOpen = true;
    // Re-picking the chassis whose card was just dismissed has to bring it back,
    // so an unchanged selection still repaints rather than returning early.
    if (deviceId === currentModel.selectedDeviceId) {
      paintSelection();
      onSelect(deviceId);
      return;
    }
    currentModel = chassisPageModel({
      snapshot: { assets: currentModel.assets, stale: currentModel.stale, warning: currentModel.warning, fetched_at: currentModel.fetchedAt },
      query: currentModel.query,
      statusFilter: currentModel.statusFilter,
      selectedDeviceId: deviceId,
      error: currentModel.error,
    });
    paintSelection();
    onSelect(deviceId);
  }

  host.addEventListener('click', (event) => {
    const option = event.target.closest?.('[data-chassis-filter-value]');
    if (option) {
      host.querySelector('[data-chassis-filter]')?.removeAttribute('open');
      onFilter(option.dataset.chassisFilterValue);
      return;
    }
    const row = event.target.closest?.('[data-chassis-row]');
    if (row) {
      select(row.dataset.chassisRow);
      return;
    }
    const refresh = event.target.closest?.('[data-chassis-refresh]');
    if (!refresh) return;
    refresh.disabled = true;
    const original = refresh.textContent;
    refresh.textContent = copy.refreshing;
    Promise.resolve()
      .then(() => onRefresh())
      .finally(() => {
        if (destroyed) return;
        refresh.disabled = false;
        refresh.textContent = original;
      });
  }, { signal });

  const search = host.querySelector('[data-chassis-search]');
  search?.addEventListener('input', () => onSearch(search.value), { signal });

  // A map of an empty region is worse than no map: it reads as "nothing is out
  // there" when the truth is that nothing was returned. With no plottable
  // chassis the canvas is put away and the reason is stated instead.
  const plottable = !currentModel.error && currentModel.assets.some((asset) => asset.mapped);
  const idleReason = currentModel.error
    ? copy.error
    : currentModel.counts.total === 0 ? copy.empty : copy.noFix;

  const ready = plottable ? Promise.resolve()
    .then(() => loader({ apiKey }))
    .then(async (library) => {
      if (destroyed) return;
      const mapsLibrary = typeof library.importLibrary === 'function'
        ? await library.importLibrary('maps')
        : library;
      maps = library;
      const MapClass = mapsLibrary?.Map || library.Map;
      if (typeof MapClass !== 'function') throw new Error('MAPS_RESPONSE_INVALID');
      const InfoWindowClass = mapsLibrary?.InfoWindow || library.InfoWindow;
      if (typeof InfoWindowClass === 'function') {
        // headerDisabled drops Google's own title bar and close button so the
        // card is ours end to end. Older builds ignore the option, in which
        // case their close button simply sits alongside ours.
        infoWindow = new InfoWindowClass({ headerDisabled: true, maxWidth: 400 });
      }
      const view = root?.ownerDocument?.defaultView || globalThis;
      map = new MapClass(canvas, {
        center: FALLBACK_CENTER,
        zoom: 10,
        ...mapDisplayOptions({ compact: Boolean(view?.matchMedia?.('(max-width: 720px)')?.matches) }),
        // Clicking bare map dismisses the card. With no close button on the
        // card itself, this is the only way back to an unobstructed map, so it
        // is a requirement rather than a convenience.
        clickableIcons: false,
        // Satellite, with labels kept on. A chassis is found by what is around
        // it — which yard, which row, which side of the terminal — and a road
        // map draws none of that. Labels stay because imagery alone loses the
        // street names needed to send a driver there. No type switcher: this
        // page is the satellite view (Alan, 2026-08-04). The shared
        // mapDisplayOptions are left alone, since the address map on the
        // container page is a different job and was not asked to change.
        mapTypeId: 'hybrid',
        mapTypeControl: false,
        // The wheel zooms on hover, no modifier key. The shared default is
        // 'cooperative', which demands ctrl+scroll so a map embedded mid-article
        // does not hijack the page scroll — but here the map IS the page, there
        // is nothing behind it to scroll, and the key press is pure friction.
        gestureHandling: 'greedy',
        // Far enough out, the world stops filling the frame: it shrinks to a
        // strip floating in grey and repeats sideways, so the same chassis
        // appears a dozen times across a dozen copies of Earth. The floor is the
        // last zoom at which the map still covers a wide canvas, and the
        // restriction keeps the view on the one real world rather than letting
        // it wander into the copies or into the void past the poles.
        minZoom: 3,
        restriction: {
          latLngBounds: { north: 85, south: -85, east: 180, west: -180 },
          strictBounds: false,
        },
      });
      map.addListener?.('click', () => {
        detailOpen = false;
        infoWindow?.close();
      });
      drawMarkers();
    })
    .catch((error) => {
      if (destroyed) return;
      map = null;
      maps = null;
      canvas.dataset.mapUnavailable = 'true';
      setStatus(`${mapLoadMessage(error)} ${copy.mapListOnly}`, 'error');
    })
    : Promise.resolve().then(() => {
      canvas.hidden = true;
      setStatus(idleReason, currentModel.error ? 'error' : 'empty');
    });

  paintDetail();

  return Object.freeze({
    ready,
    model: () => currentModel,
    select,
    update(nextModel) {
      if (destroyed || !nextModel) return;
      currentModel = nextModel;
      const railBody = host.querySelector('[data-chassis-rail-body]');
      if (railBody) railBody.innerHTML = chassisRailBodyMarkup({ model: currentModel, locale });
      // Repainted separately from the rail body so the search field beside it
      // keeps focus, and separately from the whole rail so the filter's own
      // label and counts stay in step with what was chosen.
      const filterHost = host.querySelector('[data-chassis-filter-host]');
      if (filterHost) filterHost.innerHTML = chassisFilterMarkup({ model: currentModel, locale });
      drawMarkers();
      paintSelection();
    },
    destroy() {
      destroyed = true;
      controller.abort();
      infoWindow?.close();
      infoWindow = null;
      for (const marker of markers.values()) marker.setMap?.(null);
      markers.clear();
      map = null;
      maps = null;
    },
  });
}
